import React, { useState, useEffect, useContext } from 'react'
import { EmptyPageCenter, Page } from '../Elements/PageMargins'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate, useLocation } from 'react-router'
import ProgramFormCard from '../Card/ProgramFormCard'
import { Program, ProgramType } from '../../swagger'
import TitleContext from '../../TitleContext'
import { SnackbarSeverity } from '../Alerts/SnackbarAlert'
import DynamicBreadcrumbs from '../Elements/DynamicBreadcrumbs'
import { Ability } from '@casl/ability'
import { extractedErrorObject, programs } from '../../api/swagger'
import EmptyProgramDetails from './EmptyProgramDetails'
import { useAuth } from '../Routes/AuthProvider'
import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import Tab from '@mui/material/Tab'
import { CanAccess } from '../Elements/Access'
import EnrollmentsTable from '../Families/EnrollmentsTable'
import { useEnrollmentOptions } from '../../hooks/useEnrollmentOptions'
import EnrollmentsInviteTable from './EnrollmentsInviteTable'
import DropdownSelectOptions from '../Interfaces/DropdownSelectOptions'
import { useSnackbarContext } from '../Context/SnackbarContext'
import CircularProgress from '@mui/material/CircularProgress'
import useLoadingContext from '../../hooks/useLoadingContext'
import { Box } from '@mui/material'
import { useProgramDetailsContext } from '../Context/ProgramDetailsContext'
import { useLoadingIds } from '../../hooks/useLoadingIds'
import { ACTING_AS_PARENT } from '../../utils/constants'
import { useUnmountEffect } from '../../hooks/useUnmountEffect'
import { ProgramFormCardVariants } from '../Card/ProgramFormCard/types'

export enum ProgramDetailsTab {
  programDetails = '1',
  enrollments = '2',
  invites = '4',
}

const ProgramDetails: React.FC = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { permissionAbility, userDetails } = useAuth()
  const title = t('Programs.ProgramDetails.Header', 'Programs')
  const { setSnackbarMessage, setSnackbarState, setSnackbarSeverity } =
    useSnackbarContext()
  const location: {
    state: {
      academicYear: string
      initiallySelectedTab: string
      tabToChange: string
    }
    pathname: string
  } = useLocation()

  let { academicYear } = location.state ?? {}

  const {
    ProgramDetails: {
      fetchProgram: fetchedProgramDetailsLoadingId,
      fetchEnrollments: fetchEnrollmentsLoadingId,
    },
  } = useLoadingIds()
  const { useTitleEffect } = useContext(TitleContext)

  const selectTabLocation = () => {
    switch (true) {
      case /\/enrollments/.test(location.pathname):
        return ProgramDetailsTab.enrollments
      case /\/invites/.test(location.pathname):
        return ProgramDetailsTab.invites
      default:
        return ProgramDetailsTab.programDetails
    }
  }

  const selectedTab = selectTabLocation()
  useTitleEffect(title)

  const {
    enrollmentFilters,
    enrollments,
    enrollmentsPagination,
    hasEnrollmentInvitesWithInProgressStatus,
    searchEnrollments,

    fetchEnrollments,
    handleEnrollmentsPageChange,
    handleEnrollmentsPageSizeChange,
    updateEnrollmentFilters,
    updateSearchEnrollments,
    resetContextToDefaults,
  } = useProgramDetailsContext()

  /** State Objects */
  const [programType, setProgramType] = useState<ProgramType>()

  const [programDetails, setProgramDetails] = useState<
    Program | null | undefined
  >(null)
  const [initialProgramDetails, setInitialProgramDetails] = useState<
    Program | undefined
  >(undefined)

  const { programKey, communityKey } = useParams<{
    programKey: string
    communityKey: string
  }>()
  const programId = parseInt(`${programKey}`)
  const [fetchInitialDetails, setFetchInitialDetails] = useState(true)
  const [programAbility, setProgramAbility] = useState<Ability>()

  const [errorMessage, setErrorMessage] = useState('')
  const [loadEnrollments, setLoadEnrollments] = useState(true)

  const [isProgramDetailsEdit, setIsProgramDetailsEdit] = useState(false)

  const isTabInvitesTourCompleted = localStorage.getItem(
    'joyride-programDetails-enrollmentInvitesTable-complete'
  )

  const baseUrlProgramsDetails = `/communities/community-details/${communityKey}/programs/program-details/${programKey}`

  const setSelectedTab = React.useCallback(
    (newValue: string) => {
      if (newValue === selectedTab) return
      switch (newValue) {
        case ProgramDetailsTab.enrollments:
          navigate(`enrollments`)
          break
        case ProgramDetailsTab.invites:
          navigate(`invites`)
          break
        default:
          navigate(`${baseUrlProgramsDetails}`)
          return
      }
    },
    [selectedTab, navigate, baseUrlProgramsDetails]
  )

  useUnmountEffect(resetContextToDefaults)

  /** Other Objects */
  const defaultErrorMessage = t(
    'ProgramDetails.Error.FetchProgramDetails',
    'Error occurred while retrieving program details'
  )

  const programTitle = t(
    'ProgramDetails.TabTitle.ProgramTitle',
    'Program Details'
  )

  const enrollmentsTitle = t(
    'ProgramDetails.TabTitle.EnrollmentsTitle',
    'Enrollments'
  )

  const invitesTitle = t('ProgramDetails.TabTitle.InvitesTitle', 'Invites')

  const fetchProgramDetails = async () => {
    try {
      // Fetched community name and address are random. Always in Cali and with Sally Ride
      const fetchedProgramDetails = await programs.fetchProgram({ programId })
      setProgramDetails(fetchedProgramDetails ?? undefined)
      setInitialProgramDetails(fetchedProgramDetails)
      if (!!fetchedProgramDetails) {
        setProgramType(fetchedProgramDetails.type)
      }
      setProgramAbility(
        new Ability(
          fetchedProgramDetails?.meta?.permissions?.map(
            ({ resourceCode: subject, actionCode: action }) => ({
              subject,
              action,
            })
          )
        )
      )
    } catch (e) {
      const errorObject = (await extractedErrorObject(e)) ?? {
        code: 'Unknown',
        message: (e as unknown as Error).message ?? defaultErrorMessage,
      }
      setErrorMessage(errorObject.message)
    } finally {
      removeLoadingIds([fetchedProgramDetailsLoadingId])
    }
  }

  const {
    triggerFetch: triggerFetchProgramDetails,
    removeLoadingIds,
    loadingIds,
    addLoadingIds,
  } = useLoadingContext({
    loadingId: fetchedProgramDetailsLoadingId,
    asyncFunction: fetchProgramDetails,
  })

  /** Hooks */
  useEffect(() => {
    if (fetchInitialDetails) {
      triggerFetchProgramDetails()
      setFetchInitialDetails(false)
    }
  }, [
    programId,
    fetchInitialDetails,
    defaultErrorMessage,
    triggerFetchProgramDetails,
  ])

  /**
   * Fetch Enrollments
   */

  const refetchEnrollmentsData = () => {
    // Programmatically trigger a refetch of enrollments
    setLoadEnrollments(true)
  }

  const fetchProgramEnrollments = async () => {
    try {
      await fetchEnrollments(programId)
    } catch (e) {
      const errorObject = (await extractedErrorObject(e)) ?? {
        code: 'Unknown Code',
        message: (e as unknown as Error).message ?? defaultErrorMessage,
      }
      setErrorMessage(errorObject.message)
    }
  }

  // FIXME: refactor fetchEnrollments use to not have a func in this component
  const { triggerFetch: triggerFetchProgramEnrollments } = useLoadingContext({
    loadingId: fetchEnrollmentsLoadingId,
    asyncFunction: fetchProgramEnrollments,
  })

  useEffect(() => {
    if (loadEnrollments) {
      // Only fetch enrollments if user has permission
      if (permissionAbility.can('view', 'Enrollment')) {
        triggerFetchProgramEnrollments()
      }
      setLoadEnrollments(false)
    }
  }, [
    permissionAbility,
    defaultErrorMessage,
    programId,
    loadEnrollments,
    triggerFetchProgramEnrollments,
  ])

  useEffect(() => {
    if (userDetails.actingAs !== ACTING_AS_PARENT) {
      addLoadingIds([fetchEnrollmentsLoadingId])
    }
  }, [
    searchEnrollments,
    enrollmentFilters,
    enrollmentsPagination.page,
    enrollmentsPagination.pageSize,
    userDetails.actingAs,
    addLoadingIds,
    fetchEnrollmentsLoadingId,
  ])

  const enrollmentOptions = useEnrollmentOptions({
    permissionAbility: permissionAbility,
    programKey: programId,
  })

  /**
   * If error fetching enrollments, display snack bar alert
   */
  useEffect(() => {
    if (!!errorMessage) {
      setSnackbarState(true)
      setSnackbarMessage(errorMessage)
      setSnackbarSeverity(SnackbarSeverity.Error)
    }
  })

  const updateSelectedTab = (newTab: string) => {
    setSelectedTab(newTab)
  }

  const handleSave = () => {
    setFetchInitialDetails(true)
  }

  const onCommunitiesBreadCrumbClick = () => {
    navigate({ pathname: '/communities' })
  }

  //If no academic year from location, set academic year value from the program semesterOneStartDate
  academicYear =
    academicYear ??
    new Date(programDetails?.semesterOneStartDate as Date)
      .getUTCFullYear()
      .toString()

  const communityBreadcrumbLabel = `${programDetails?.communityName} (${academicYear})`

  const onCommunityBreadCrumbClick = () => {
    const selectedYearQueryString = !!academicYear
      ? new URLSearchParams({ year: academicYear }).toString()
      : ''

    navigate(
      {
        pathname: `/communities/community-details/${programDetails?.communityId}/overview`,
        search: selectedYearQueryString,
      },
      {
        state: {
          communityKey: programDetails?.communityId,
          communityName: programDetails?.communityName,
        },
      }
    )
  }

  const labelForBreadcrumb = () => {
    let label
    switch (programType) {
      case ProgramType.Scribblers:
        label = t(
          'Programs.ProgramDetails.BreadCrumb.ScribblersDetails',
          'Scribblers'
        )
        break
      case ProgramType.Foundations:
        label = t(
          'Programs.ProgramDetails.BreadCrumb.FoundationsDetails',
          'Foundations'
        )
        break
      case ProgramType.Essentials:
        label = t(
          'Programs.ProgramDetails.BreadCrumb.EssentialsDetails',
          'Essentials'
        )
        break
      case ProgramType.ChallengeA:
        label = t(
          'Programs.ProgramDetails.BreadCrumb.ChallengeADetails',
          'Challenge A'
        )
        break
      case ProgramType.ChallengeB:
        label = t(
          'Programs.ProgramDetails.BreadCrumb.ChallengeBDetails',
          'Challenge B'
        )
        break
      case ProgramType.ChallengeI:
        label = t(
          'Programs.ProgramDetails.BreadCrumb.ChallengeIDetails',
          'Challenge I '
        )
        break
      case ProgramType.ChallengeIi:
        label = t(
          'Programs.ProgramDetails.BreadCrumb.ChallengeIIDetails',
          'Challenge II'
        )
        break
      case ProgramType.ChallengeIii:
        label = t(
          'Programs.ProgramDetails.BreadCrumb.ChallengeIIIDetails',
          'Challenge III'
        )
        break
      case ProgramType.ChallengeIv:
        label = t(
          'Programs.ProgramDetails.BreadCrumb.ChallengeIVDetails',
          'Challenge IV'
        )
        break
      default:
        label = ''
    }
    return label
  }

  const setProgramDetailsForParent = (program?: Program) => {
    setProgramDetails(program ?? undefined)
  }

  const updateProgramDetailsEditMode = (isEdit: boolean) => {
    setIsProgramDetailsEdit(isEdit)
  }

  // 1) Not loaded yet ,initial state before fetch finishes
  if (programDetails === null) {
    return <EmptyProgramDetails isLoading />
  }
  // 2) Loaded but empty/not found
  if (programDetails === undefined) {
    return <EmptyProgramDetails isLoading={false} />
  }
  const Tabs = [
    <Tab
      key="1"
      label={programTitle}
      value={ProgramDetailsTab.programDetails}
    />,
    ...(permissionAbility.can('view', 'Enrollment')
      ? [
          <Tab
            key="2"
            label={enrollmentsTitle}
            value={ProgramDetailsTab.enrollments}
          />,
        ]
      : []),
    ...(permissionAbility.can('invite', 'Family')
      ? [<Tab key="4" label={invitesTitle} value={ProgramDetailsTab.invites} />]
      : []),
  ]

  const programBreadCrumb = [
    {
      label: t('Programs.ProgramDetails.BreadCrumb', 'Communities'),
      onClick: onCommunitiesBreadCrumbClick,
    },
    {
      label: communityBreadcrumbLabel,
      onClick: onCommunityBreadCrumbClick,
    },
    {
      label: labelForBreadcrumb(),
    },
  ]

  return (
    <TabContext value={selectedTab}>
      <TabList
        value={selectedTab}
        onChange={(_, newValue) => setSelectedTab(newValue)}
        textColor="primary"
        variant="scrollable"
        scrollButtons={true}
      >
        {Tabs}
      </TabList>
      <TabPanel value={ProgramDetailsTab.programDetails}>
        <Page withinTab>
          <DynamicBreadcrumbs breadcrumbs={programBreadCrumb} />
          <Box
            sx={{
              color: theme.palette.primary.main,
              maxWidth: 1200,
              [theme.breakpoints.down('sm')]: {
                flexDirection: 'column',
                display: 'flex',
              },
            }}
          >
            <section aria-label="programFormCard">
              {loadingIds.has(fetchedProgramDetailsLoadingId) ? (
                <EmptyPageCenter adjustForMargins>
                  <CircularProgress />
                </EmptyPageCenter>
              ) : (<ProgramFormCard
                variant={ProgramFormCardVariants.EditProgram}
                totalEnrolledStudents={
                  enrollmentsPagination
                    ? enrollmentsPagination.totalCount
                    : enrollments.length
                }
                programDetails={programDetails}
                handleSave={handleSave}
                programAbility={programAbility}
                setEditForParent={updateProgramDetailsEditMode}
                hasEnrollment={enrollments.length > 0 ? true : false}
                updateSelectedTab={updateSelectedTab}
                initialProgramDetails={initialProgramDetails}
                setProgramDetailsForParent={setProgramDetailsForParent}
                isProgramDetailsEdit={isProgramDetailsEdit}
                hasEnrollmentInvitesWithInProgressStatus={
                  hasEnrollmentInvitesWithInProgressStatus
                }
                isProgramDetailsLoading
              />
              )}
            </section>
          </Box>
        </Page>
      </TabPanel>
      <TabPanel value={ProgramDetailsTab.enrollments}>
        <Page withinTab>
          <DynamicBreadcrumbs breadcrumbs={programBreadCrumb} />
          <CanAccess I="view" on="Enrollment">
            {loadingIds.has(fetchEnrollmentsLoadingId) ? (
              <EmptyPageCenter adjustForMargins>
                <CircularProgress />
              </EmptyPageCenter>
            ) : (
              <section aria-label="enrollments">
                <EnrollmentsTable
                  pagination={enrollmentsPagination}
                  handleChangePage={handleEnrollmentsPageChange}
                  handleChangePageSize={handleEnrollmentsPageSizeChange}
                  searchText={searchEnrollments}
                  setSearchState={updateSearchEnrollments}
                  filters={enrollmentFilters}
                  setFilters={updateEnrollmentFilters}
                  enrollments={enrollments}
                  enrollmentOptions={enrollmentOptions?.statuses ?? []}
                  refetchEnrollmentsData={refetchEnrollmentsData}
                  tutors={
                    programDetails.tutors?.map((it) => {
                      return {
                        id: it.actorKey,
                        label: it.tutorName,
                      } as DropdownSelectOptions
                    }) ?? []
                  }
                  programInfo={{
                    programKey: programDetails.programsKey,
                    programType: programDetails.type,
                    status: programDetails.status,
                    firstSemesterStartDate: programDetails.semesterOneStartDate,
                  }}
                  academicYear={academicYear}
                  programAbility={
                    new Ability(
                      programDetails.meta?.permissions?.map(
                        ({ resourceCode: subject, actionCode: action }) => ({
                          subject,
                          action,
                        })
                      )
                    )
                  }
                  communityId={programDetails.communityId}
                  setEditForParent={updateProgramDetailsEditMode}
                  programEmailDirector={programDetails.directorEmail}
                />
              </section>
            )}
          </CanAccess>
        </Page>
      </TabPanel>
      <TabPanel value={ProgramDetailsTab.invites}>
        <Page withinTab>
          <DynamicBreadcrumbs breadcrumbs={programBreadCrumb} />
          <EnrollmentsInviteTable
            isTabInvitesTourCompleted={
              isTabInvitesTourCompleted as unknown as boolean
            }
          />
        </Page>
      </TabPanel>
    </TabContext>
  )
}

export default ProgramDetails
